import { setBaseApiToken } from '@/services/api';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export const roles = [
  'super_admin',
  'org_admin',
  'org_user',
  'workspace_admin',
  'workspace_user',
] as const

export type Role = (typeof roles)[number]

interface UserOrganisation {
  id: string;
  role: Role;
  verified: boolean;
  workspaces: string[];
}

interface UserWorkspace {
  role: Role;
  workspaceId: string;
}

interface User {
  id: string;
  email: string;
  organisation?: UserOrganisation | null;
  workspace?: UserWorkspace | null;
}

interface AuthState {
  token: string | null;
  user: User | null;
  setAuthData: (token: string, user: User) => void;
  clearAuthData: () => void;
  activeWorkspace: string | null;
  context: 'organisation' | 'workspace' | null;
  setActiveWorkspace: (workspace: string) => void;
  setUserOrganisationVerified: (verified: boolean) => void;
  showSpotlightModal: boolean;
  setShowSpotlightModal: (show: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      activeWorkspace: null,
      context: null,
      showSpotlightModal: false,
  
      setAuthData: (token, user) => {
        let context: AuthState['context'] = null;
        let activeWorkspace: string | null = null;

        if (user.organisation) {
          context = 'organisation';
          activeWorkspace = user.organisation.workspaces[0] ?? null;
        } else if (user.workspace) {
          context = 'workspace';
          activeWorkspace = user.workspace.workspaceId ?? null;
        }
        
        set({
          token,
          user,
          context,
          activeWorkspace,
        });

        setBaseApiToken(`Bearer ${token}`);
      },

      setUserOrganisationVerified: (verified: boolean) => {
        const currentUser = get().user;
        if (!currentUser?.organisation) return;
        
        set({ 
          user: { 
            ...currentUser,
            organisation: {
              ...currentUser.organisation,
              verified,
            }
          } 
        });
      },

      setActiveWorkspace: (workspace) => {
        set({ activeWorkspace: workspace });
      },
      clearAuthData: () => {
        set({ token: null, user: null, activeWorkspace: null, context: null, showSpotlightModal: false });
        setBaseApiToken('');
      },

      setShowSpotlightModal: (show: boolean) => {
        set({ showSpotlightModal: show });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          setBaseApiToken(`Bearer ${state.token}`);
        }
      },
    }
  )
);
